import { ContentAttachmentsEditor } from '@/components/content/ContentAttachmentsEditor';
import { useState, lazy, Suspense } from "react";
import { Checkbox } from "@/components/ui/checkbox";
import { useRole } from "@/contexts/RoleContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Plus, Pencil, Trash2, Video, Search, Filter, FileText,
  Headphones, BookOpen, Image, AlertTriangle, ArrowUpDown,
  ArrowUp, ArrowDown, FolderOpen, RefreshCw, Eye,
} from "lucide-react";
import { ContentPlayerModal } from "@/components/ContentPlayerModal";
import { CONTENT_TYPE_MAP } from "@/lib/contentTypeMap";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useContentBank, useCreateContent, useUpdateContent, useDeleteContent } from "@/hooks/useContent";
import { useContentCategories } from "@/hooks/useContentCategories";
import { Skeleton } from "@/components/ui/skeleton";
import { VideoUploadSection } from "@/components/admin/VideoUploadSection";
import { FileUploadSection } from "@/components/admin/FileUploadSection";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ContentPlanningPanel } from "@/components/admin/planning/ContentPlanningPanel";
import { ContentProgramsBadge } from "@/components/admin/content/ContentProgramsBadge";
import { VideoTranscriptsPanel } from "@/components/admin/content/VideoTranscriptsPanel";

const RichTextEditor = lazy(() => import("@/components/RichTextEditor"));

const accessLevels = [
  { value: "free", label: "Gratis" },
  { value: "subscription", label: "Suscripción" },
  { value: "program_only", label: "Solo ruta" },
];

const tiposContenido = ["Video", "Interactivo", "Infografía", "Clase con LIA", "Recurso", "Lectura"];

interface FormData {
  name: string;
  content_type: string;
  duration: string;
  description: string;
  url: string;
  tags: string;
  access_level: string;
  thumbnail_url: string;
  author: string;
  vimeo_id: string;
  vimeo_uri: string;
  vimeo_player_url: string;
  html_content: string;
  category: string;
  target_levels: number[];
  sort_order: number;
}

const emptyForm: FormData = {
  name: "",
  content_type: "",
  duration: "",
  description: "",
  url: "",
  tags: "",
  access_level: "free",
  thumbnail_url: "",
  author: "",
  vimeo_id: "",
  vimeo_uri: "",
  vimeo_player_url: "",
  html_content: "",
  category: "",
  target_levels: [],
  sort_order: 0,
};

const LEVEL_COLORS: Record<number, string> = {
  0: 'bg-gray-100 text-gray-800 dark:bg-gray-800/30 dark:text-gray-300 border-gray-200 dark:border-gray-700',
  1: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300 border-emerald-200 dark:border-emerald-800',
  2: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300 border-blue-200 dark:border-blue-800',
  3: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300 border-purple-200 dark:border-purple-800',
  4: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300 border-amber-200 dark:border-amber-800',
  5: 'bg-rose-100 text-rose-800 dark:bg-rose-900/30 dark:text-rose-300 border-rose-200 dark:border-rose-800',
};

const contentTypeIcon = (tipo: string | null) => {
  switch (tipo) {
    case "Video": return <Video className="h-4 w-4 text-muted-foreground" />;
    case "Audio": return <Headphones className="h-4 w-4 text-muted-foreground" />;
    case "Lectura": return <BookOpen className="h-4 w-4 text-muted-foreground" />;
    default: return <FileText className="h-4 w-4 text-muted-foreground" />;
  }
};

const accessLabel = (val: string) => accessLevels.find((l) => l.value === val)?.label ?? val;

// --- Color helpers ---
const contentTypeBadgeClass = (tipo: string | null): string => {
  switch (tipo) {
    case "Video": return "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300 border-blue-200 dark:border-blue-800";
    case "Audio": return "bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300 border-purple-200 dark:border-purple-800";
    case "Lectura": return "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300 border-green-200 dark:border-green-800";
    case "Clase con LIA": return "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300 border-amber-200 dark:border-amber-800";
    case "Infografía": return "bg-pink-100 text-pink-800 dark:bg-pink-900/30 dark:text-pink-300 border-pink-200 dark:border-pink-800";
    case "Interactivo": return "bg-cyan-100 text-cyan-800 dark:bg-cyan-900/30 dark:text-cyan-300 border-cyan-200 dark:border-cyan-800";
    case "Recurso": return "bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300 border-orange-200 dark:border-orange-800";
    default: return "";
  }
};

const accessBadgeClass = (val: string): string => {
  switch (val) {
    case "free": return "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300 border-green-200 dark:border-green-800";
    
    case "subscription": return "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300 border-amber-200 dark:border-amber-800";
    case "program_only": return "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300 border-blue-200 dark:border-blue-800";
    default: return "";
  }
};

// --- Incomplete check ---
const isContentComplete = (item: { content_type: string | null; vimeo_id: string | null; url: string | null; html_content: string | null; thumbnail_url: string | null }) => {
  switch (item.content_type) {
    case "Video": return !!item.vimeo_id;
    case "Audio": return !!item.url; // legacy
    case "Recurso": return !!item.url;
    case "Lectura": return !!item.html_content;
    case "Clase con LIA": return !!item.html_content;
    case "Infografía": return !!item.thumbnail_url;
    case "Interactivo": return !!item.url;
    default: return true;
  }
};

type SortOrder = "asc" | "desc" | null;

export default function ContentManagementPage() {
  const { currentRole } = useRole();
  const { data: allContents = [], isLoading } = useContentBank();
  // El banco de contenidos (primer tab) solo muestra los publicados.
  // Los borradores/planificados se gestionan en el tab de Planificación.
  const contents = (allContents as any[]).filter((c) => c.planning_status === 'published');
  const createMutation = useCreateContent();
  const updateMutation = useUpdateContent();
  const deleteMutation = useDeleteContent();
  const { data: contentCategories } = useContentCategories();

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formData, setFormData] = useState<FormData>(emptyForm);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterTipo, setFilterTipo] = useState("all");
  const [filterAccess, setFilterAccess] = useState("all");
  const [filterCategory, setFilterCategory] = useState("all");
  const [sortOrder, setSortOrder] = useState<SortOrder>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [syncingThumbs, setSyncingThumbs] = useState(false);
  const [previewItem, setPreviewItem] = useState<typeof contents[number] | null>(null);
  const [editingOrderId, setEditingOrderId] = useState<string | null>(null);
  const [orderDraft, setOrderDraft] = useState<string>("");

  const commitOrder = async (id: string) => {
    const newVal = Number(orderDraft);
    const safe = Number.isFinite(newVal) ? newVal : 0;
    try {
      await updateMutation.mutateAsync({ id, sort_order: safe } as any);
      toast.success("Orden actualizado");
    } catch {
      toast.error("Error al actualizar el orden");
    } finally {
      setEditingOrderId(null);
      setOrderDraft("");
    }
  };

  const missingThumbsCount = (allContents as any[]).filter(
    (c) => (c.vimeo_id || c.vimeo_player_url) && (!c.thumbnail_url || !c.duration)
  ).length;

  const handleSyncVimeoThumbs = async () => {
    setSyncingThumbs(true);
    try {
      const { data, error } = await supabase.functions.invoke("vimeo-upload", {
        body: { action: "backfill-thumbnails" },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      toast.success(
        `Portadas sincronizadas: ${data.updated} actualizadas, ${data.failed} fallidas (de ${data.processed})`
      );
      // Refresh content list
      window.location.reload();
    } catch (e: any) {
      toast.error(`Error sincronizando portadas: ${e.message}`);
    } finally {
      setSyncingThumbs(false);
    }
  };

  if (currentRole !== "super_admin") {
    return (
      <div className="p-8 text-center">
        <h1 className="text-2xl font-bold text-destructive">Acceso Denegado</h1>
        <p className="text-muted-foreground mt-2">Solo los super administradores pueden acceder a esta página.</p>
      </div>
    );
  }

  const handleOpen = (item?: typeof contents[number]) => {
    if (item) {
      setEditingId(item.id);
      setFormData({
        name: item.name,
        content_type: item.content_type ?? "",
        duration: item.duration ?? "",
        description: item.description ?? "",
        url: item.url ?? "",
        tags: (item.tags ?? []).join(", "),
        access_level: item.access_level ?? "free",
        thumbnail_url: item.thumbnail_url ?? "",
        author: item.author ?? "",
        vimeo_id: item.vimeo_id ?? "",
        vimeo_uri: item.vimeo_uri ?? "",
        vimeo_player_url: item.vimeo_player_url ?? "",
        html_content: (item as any).html_content ?? "",
        category: (item as any).category ?? "",
        target_levels: (item as any).target_levels ?? [],
        sort_order: (item as any).sort_order ?? 0,
      });
    } else {
      setEditingId(null);
      setFormData(emptyForm);
    }
    setIsDialogOpen(true);
  };

  const handleClose = () => {
    setIsDialogOpen(false);
    setEditingId(null);
    setFormData(emptyForm);
  };

  const handleSave = async () => {
    if (!formData.name || !formData.content_type) {
      toast.error("El nombre y el tipo de contenido son obligatorios");
      return;
    }
    // Salvaguarda: si la URL es de Vimeo y no hay vimeo_id, autocompletar campos vimeo_*
    let vimeo_id = formData.vimeo_id;
    let vimeo_uri = formData.vimeo_uri;
    let vimeo_player_url = formData.vimeo_player_url;
    if (formData.content_type === "Video" && !vimeo_id && formData.url) {
      const m = formData.url.match(/vimeo\.com\/(?:video\/|channels\/[^/]+\/)?(\d+)/);
      if (m) {
        vimeo_id = m[1];
        vimeo_uri = `/videos/${m[1]}`;
        vimeo_player_url = `https://player.vimeo.com/video/${m[1]}`;
      }
    }

    const payload: any = {
      name: formData.name,
      content_type: formData.content_type,
      duration: formData.duration || null,
      description: formData.description || null,
      url: formData.url || null,
      tags: formData.tags ? formData.tags.split(",").map((t) => t.trim()).filter(Boolean) : null,
      access_level: formData.access_level,
      thumbnail_url: formData.thumbnail_url || null,
      author: formData.author || null,
      vimeo_id: vimeo_id || null,
      vimeo_uri: vimeo_uri || null,
      vimeo_player_url: vimeo_player_url || null,
      html_content: formData.html_content || null,
      category: formData.category || null,
      target_levels: formData.target_levels.length > 0 ? formData.target_levels : [],
      sort_order: formData.sort_order ?? 0,
    };

    try {
      if (editingId) {
        await updateMutation.mutateAsync({ id: editingId, ...payload });
        toast.success("Contenido actualizado correctamente");
      } else {
        await createMutation.mutateAsync(payload);
        toast.success("Contenido creado correctamente");
      }
      handleClose();
    } catch {
      toast.error("Error al guardar el contenido");
    }
  };

  const handleConfirmDelete = async () => {
    if (!deletingId) return;
    try {
      await deleteMutation.mutateAsync(deletingId);
      toast.success("Contenido eliminado correctamente");
    } catch {
      toast.error("Error al eliminar el contenido");
    }
    setDeletingId(null);
  };

  // --- Filter & Sort ---
  let filtered = contents.filter((c) => {
    const search = searchTerm.toLowerCase();
    const matchesSearch =
      c.name.toLowerCase().includes(search) ||
      (c.tags ?? []).join(" ").toLowerCase().includes(search) ||
      (c.description ?? "").toLowerCase().includes(search);
    const matchesTipo = filterTipo === "all" || c.content_type === filterTipo;
    const matchesAccess = filterAccess === "all" || c.access_level === filterAccess;
    const cat = (c as any).category ?? "";
    const matchesCategory =
      filterCategory === "all" ||
      (filterCategory === "__none__" ? !cat : cat === filterCategory);
    return matchesSearch && matchesTipo && matchesAccess && matchesCategory;
  });

  if (sortOrder) {
    filtered = [...filtered].sort((a, b) => {
      const cmp = a.name.localeCompare(b.name, "es");
      return sortOrder === "asc" ? cmp : -cmp;
    });
  } else {
    filtered = [...filtered].sort((a, b) => {
      const ao = (a as any).sort_order ?? 0;
      const bo = (b as any).sort_order ?? 0;
      if (ao !== bo) return ao - bo;
      return a.name.localeCompare(b.name, "es");
    });
  }

  const toggleSort = () => {
    setSortOrder((prev) => (prev === null ? "asc" : prev === "asc" ? "desc" : null));
  };

  const tipoCount = (tipo: string) => contents.filter((c) => c.content_type === tipo).length;
  const publicCount = contents.filter((c) => c.access_level === "free").length;
  const isSaving = createMutation.isPending || updateMutation.isPending;
  const showDuration = !["Lectura", "Infografía"].includes(formData.content_type);
  const deletingItem = contents.find((c) => c.id === deletingId);

  return (
    <div className="space-y-6">
      <Tabs defaultValue="bank" className="space-y-6">
        <TabsList>
          <TabsTrigger value="bank">Banco de Contenidos</TabsTrigger>
          <TabsTrigger value="planning">Planificación</TabsTrigger>
        </TabsList>

        <TabsContent value="planning" className="space-y-6">
          <ContentPlanningPanel />
        </TabsContent>

        <TabsContent value="bank" className="space-y-6">
      {currentRole === "super_admin" && <VideoTranscriptsPanel />}
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Banco de Contenidos</h1>
          <p className="text-muted-foreground mt-1">
            Gestiona todo el contenido educativo disponible para asignar a rutas
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={handleSyncVimeoThumbs}
            disabled={syncingThumbs || missingThumbsCount === 0}
            title={missingThumbsCount === 0 ? "No hay metadata pendiente" : `${missingThumbsCount} videos sin portada o duración`}
          >
            <RefreshCw className={`mr-2 h-4 w-4 ${syncingThumbs ? "animate-spin" : ""}`} />
            Sincronizar metadata Vimeo{missingThumbsCount > 0 ? ` (${missingThumbsCount})` : ""}
          </Button>
          <Button onClick={() => handleOpen()}>
            <Plus className="mr-2 h-4 w-4" />
            Nuevo Contenido
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Contenidos</CardTitle>
            <FileText className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{contents.length}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Videos</CardTitle>
            <Video className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{tipoCount("Video")}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Interactivos</CardTitle>
            <FileText className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {tipoCount("Interactivo") + tipoCount("Clase con LIA") + tipoCount("Infografía")}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Acceso Público</CardTitle>
            <BookOpen className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{publicCount}</div>
          </CardContent>
        </Card>
      </div>

      {/* Search & Filter */}
      <div className="flex flex-wrap items-center gap-4">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Buscar por nombre, etiquetas o descripción..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        <Select value={filterTipo} onValueChange={setFilterTipo}>
          <SelectTrigger className="w-[200px]">
            <Filter className="mr-2 h-4 w-4" />
            <SelectValue placeholder="Tipo de contenido" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos los tipos</SelectItem>
            {tiposContenido.map((t) => (
              <SelectItem key={t} value={t}>{t}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={filterAccess} onValueChange={setFilterAccess}>
          <SelectTrigger className="w-[200px]">
            <Filter className="mr-2 h-4 w-4" />
            <SelectValue placeholder="Nivel de acceso" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos los niveles</SelectItem>
            {accessLevels.map((l) => (
              <SelectItem key={l.value} value={l.value}>{l.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={filterCategory} onValueChange={setFilterCategory}>
          <SelectTrigger className="w-[200px]">
            <Filter className="mr-2 h-4 w-4" />
            <SelectValue placeholder="Categoría" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todas las categorías</SelectItem>
            <SelectItem value="__none__">Sin categoría</SelectItem>
            {(contentCategories ?? []).map((c) => (
              <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Results counter */}
      <p className="text-sm text-muted-foreground">
        Mostrando {filtered.length} de {contents.length} contenidos
      </p>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="p-6 space-y-3">
              {[1, 2, 3].map((i) => <Skeleton key={i} className="h-12 w-full" />)}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-20">Orden</TableHead>
                  <TableHead>
                    <button onClick={toggleSort} className="flex items-center gap-1 hover:text-foreground transition-colors">
                      Nombre
                      {sortOrder === null && <ArrowUpDown className="h-3.5 w-3.5" />}
                      {sortOrder === "asc" && <ArrowUp className="h-3.5 w-3.5" />}
                      {sortOrder === "desc" && <ArrowDown className="h-3.5 w-3.5" />}
                    </button>
                  </TableHead>
                  <TableHead>Tipo</TableHead>
                  <TableHead>Acceso</TableHead>
                  <TableHead>Niveles</TableHead>
                  <TableHead>Categoría</TableHead>
                  <TableHead>Duración</TableHead>
                  <TableHead>Etiquetas</TableHead>
                  <TableHead className="text-right">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((item) => {
                  const complete = isContentComplete(item);
                  return (
                    <TableRow key={item.id}>
                      <TableCell
                        onDoubleClick={() => {
                          setEditingOrderId(item.id);
                          setOrderDraft(String((item as any).sort_order ?? 0));
                        }}
                        className="cursor-pointer select-none"
                        title="Doble clic para editar"
                      >
                        {editingOrderId === item.id ? (
                          <Input
                            type="number"
                            autoFocus
                            value={orderDraft}
                            onChange={(e) => setOrderDraft(e.target.value)}
                            onBlur={() => commitOrder(item.id)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") { e.preventDefault(); commitOrder(item.id); }
                              else if (e.key === "Escape") { setEditingOrderId(null); setOrderDraft(""); }
                            }}
                            className="h-7 w-16"
                          />
                        ) : (
                          <span className="tabular-nums text-sm">{(item as any).sort_order ?? 0}</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          {item.thumbnail_url ? (
                            <img src={item.thumbnail_url} alt="" className="h-8 w-8 rounded object-cover" />
                          ) : (
                            contentTypeIcon(item.content_type)
                          )}
                          <div className="min-w-0">
                            <div className="font-medium flex items-center gap-1.5">
                              {item.name}
                              {!complete && (
                                <span title="Contenido incompleto: falta archivo o configuración">
                                  <AlertTriangle className="h-3.5 w-3.5 text-amber-500" />
                                </span>
                              )}
                            </div>
                            {item.description && (
                              <div className="text-xs text-muted-foreground line-clamp-1">{item.description}</div>
                            )}
                            {item.author && (
                              <div className="text-xs text-muted-foreground">por {item.author}</div>
                            )}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className={contentTypeBadgeClass(item.content_type)}>
                          {item.content_type}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className={accessBadgeClass(item.access_level)}>
                          {accessLabel(item.access_level)}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {((item as any).target_levels ?? []).length > 0
                            ? ((item as any).target_levels as number[]).sort((a: number, b: number) => a - b).map((lvl: number) => (
                                <Badge key={lvl} variant="outline" className={`text-[10px] px-1.5 py-0 ${LEVEL_COLORS[lvl] ?? ''}`}>
                                  N{lvl}
                                </Badge>
                              ))
                            : <span className="text-xs text-muted-foreground">—</span>}
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm text-muted-foreground">{(item as any).category ?? "—"}</span>
                      </TableCell>
                      <TableCell>{item.duration ?? "—"}</TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {(item.tags ?? []).slice(0, 3).map((tag, i) => (
                            <Badge key={i} variant="outline" className="text-xs">{tag}</Badge>
                          ))}
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button variant="ghost" size="icon" onClick={() => setPreviewItem(item)} title="Previsualizar">
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="icon" onClick={() => handleOpen(item)} title="Editar">
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="icon" onClick={() => setDeletingId(item.id)} disabled={deleteMutation.isPending}>
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
                {filtered.length === 0 && !isLoading && (
                  <TableRow>
                    <TableCell colSpan={9} className="text-center py-12">
                      <div className="flex flex-col items-center gap-3">
                        <FolderOpen className="h-10 w-10 text-muted-foreground/50" />
                        <div>
                          <p className="font-medium text-muted-foreground">No se encontraron contenidos</p>
                          <p className="text-sm text-muted-foreground/70 mt-1">
                            {searchTerm || filterTipo !== "all" || filterAccess !== "all"
                              ? "Prueba ajustando los filtros de búsqueda"
                              : "Crea tu primer contenido para comenzar"}
                          </p>
                        </div>
                        {!searchTerm && filterTipo === "all" && filterAccess === "all" && (
                          <Button size="sm" onClick={() => handleOpen()} className="mt-1">
                            <Plus className="mr-2 h-4 w-4" /> Crear primer contenido
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deletingId} onOpenChange={(open) => !open && setDeletingId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar este contenido?</AlertDialogTitle>
            <AlertDialogDescription>
              Se eliminará permanentemente <strong>"{deletingItem?.name}"</strong>. Esta acción no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className={`max-h-[90vh] overflow-y-auto ${formData.content_type === "Lectura" ? "max-w-2xl" : "max-w-lg"}`}>
          <DialogHeader>
            <DialogTitle>{editingId ? "Editar Contenido" : "Nuevo Contenido"}</DialogTitle>
          </DialogHeader>
          {editingId && (
            <div className="pt-2">
              <ContentProgramsBadge contentId={editingId} />
            </div>
          )}
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="name">Nombre del contenido *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Ej: Introducción a la IA Generativa"
              />
            </div>

            <div className={`grid gap-4 ${showDuration ? "grid-cols-2" : "grid-cols-1"}`}>
              <div className="grid gap-2">
                <Label>Tipo de Contenido *</Label>
                <Select
                  value={formData.content_type}
                  onValueChange={(v) => setFormData({ ...formData, content_type: v })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccionar tipo" />
                  </SelectTrigger>
                  <SelectContent>
                    {tiposContenido.map((t) => (
                      <SelectItem key={t} value={t}>{t}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {showDuration && (
                <div className="grid gap-2">
                  <Label htmlFor="duration">Duración (mm:ss)</Label>
                  <Input
                    id="duration"
                    value={formData.duration}
                    onChange={(e) => setFormData({ ...formData, duration: e.target.value })}
                    placeholder="Ej: 15:30"
                  />
                </div>
              )}
            </div>

            <div className="grid gap-2">
              <Label>Nivel de acceso</Label>
              <Select
                value={formData.access_level}
                onValueChange={(v) => setFormData({ ...formData, access_level: v })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Seleccionar nivel" />
                </SelectTrigger>
                <SelectContent>
                  {accessLevels.map((l) => (
                    <SelectItem key={l.value} value={l.value}>{l.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid gap-2">
              <Label htmlFor="category">Categoría</Label>
              <Select
                value={formData.category || "__none__"}
                onValueChange={(v) => setFormData({ ...formData, category: v === "__none__" ? "" : v })}
              >
                <SelectTrigger id="category">
                  <SelectValue placeholder="Seleccionar categoría" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">Sin categoría</SelectItem>
                  {(contentCategories ?? []).map((cat) => (
                    <SelectItem key={cat.id} value={cat.name}>{cat.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid gap-2">
              <Label htmlFor="sort_order">Orden en categoría</Label>
              <Input
                id="sort_order"
                type="number"
                min={0}
                value={formData.sort_order}
                onChange={(e) => setFormData({ ...formData, sort_order: parseInt(e.target.value) || 0 })}
                placeholder="0"
              />
            </div>

            <div className="grid gap-2">
              <Label>Niveles objetivo</Label>
              <div className="flex flex-wrap gap-3">
                {[0, 1, 2, 3, 4, 5].map((lvl) => (
                  <label key={lvl} className="flex items-center gap-1.5 cursor-pointer">
                    <Checkbox
                      checked={formData.target_levels.includes(lvl)}
                      onCheckedChange={(checked) => {
                        setFormData((prev) => ({
                          ...prev,
                          target_levels: checked
                            ? [...prev.target_levels, lvl].sort((a, b) => a - b)
                            : prev.target_levels.filter((l) => l !== lvl),
                        }));
                      }}
                    />
                    <span className="text-sm">Nivel {lvl}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="grid gap-2">
              <Label htmlFor="author">Autor</Label>
              <Input
                id="author"
                value={formData.author}
                onChange={(e) => setFormData({ ...formData, author: e.target.value })}
                placeholder="Nombre del autor"
              />
            </div>

            {/* === Type-specific sections === */}

            {formData.content_type === "Video" && (
              <VideoUploadSection
                onVideoReady={(result) => {
                  setFormData(prev => ({
                    ...prev,
                    vimeo_id: result.vimeo_id,
                    vimeo_uri: result.vimeo_uri,
                    vimeo_player_url: result.vimeo_player_url,
                    thumbnail_url: result.thumbnail_url || prev.thumbnail_url,
                    duration: result.duration || prev.duration,
                  }));
                }}
                initialVimeoId={formData.vimeo_id}
                initialPlayerUrl={formData.vimeo_player_url}
                initialThumbnailUrl={formData.thumbnail_url}
                onThumbnailRefreshed={(url) =>
                  url && setFormData(prev => ({ ...prev, thumbnail_url: url }))
                }
              />
            )}

            {formData.content_type === "Infografía" && (
              <>
                <FileUploadSection
                  accept="image/*"
                  label="Subir Infografía (imagen)"
                  currentUrl={formData.thumbnail_url || null}
                  onFileUploaded={(url) => setFormData(prev => ({ ...prev, thumbnail_url: url }))}
                  onClear={() => setFormData(prev => ({ ...prev, thumbnail_url: "" }))}
                />
                <FileUploadSection
                  accept="audio/*,.mp3,.wav,.ogg,.m4a,.aac"
                  label="Audio explicativo (opcional)"
                  currentUrl={formData.url || null}
                  onFileUploaded={(url) => setFormData(prev => ({ ...prev, url }))}
                  onClear={() => setFormData(prev => ({ ...prev, url: "" }))}
                />
              </>
            )}

            {formData.content_type === "Recurso" && (
              <FileUploadSection
                accept="*/*"
                label="Subir recurso (archivo)"
                currentUrl={formData.url || null}
                onFileUploaded={(url) => setFormData(prev => ({ ...prev, url }))}
                onClear={() => setFormData(prev => ({ ...prev, url: "" }))}
                showResourceUrl
                resourceUrl={formData.url}
                onResourceUrlChange={(url) => setFormData(prev => ({ ...prev, url }))}
              />
            )}

            {formData.content_type === "Interactivo" && (
              <>
                <FileUploadSection
                  accept=".html,.htm"
                  label="Subir archivo HTML interactivo (OVA)"
                  currentUrl={formData.url || null}
                  onFileUploaded={(url) => setFormData(prev => ({ ...prev, url }))}
                  onClear={() => setFormData(prev => ({ ...prev, url: "" }))}
                  allowHtml
                />
                <div className="grid gap-2">
                  <Label htmlFor="url" className="text-xs text-muted-foreground">O pega una URL externa</Label>
                  <Input
                    id="url"
                    value={formData.url}
                    onChange={(e) => setFormData({ ...formData, url: e.target.value })}
                    placeholder="https://..."
                  />
                </div>
              </>
            )}

            {formData.content_type === "Clase con LIA" && (
              <div className="grid gap-2">
                <Label htmlFor="html_content">Prompt del asistente (pasos de la clase)</Label>
                <p className="text-xs text-muted-foreground">
                  Escribe cada paso de la clase en una línea numerada (1. ..., 2. ..., 3. ...). LIA guiará al participante paso a paso y la lección se marcará como completada automáticamente al terminar todos los pasos. Si no usas pasos numerados, el prompt se enviará como contexto libre.
                </p>
                <Textarea
                  id="html_content"
                  value={formData.html_content}
                  onChange={(e) => setFormData({ ...formData, html_content: e.target.value })}
                  placeholder={"1. Explica qué es la inteligencia artificial generativa y pide al participante que dé un ejemplo de su área de trabajo.\n2. Presenta 3 casos de uso de IA en empresas y pregunta cuál aplicaría en su organización.\n3. Guía al participante a definir un mini-proyecto de IA con objetivo, datos y herramienta sugerida."}
                  rows={8}
                />
              </div>
            )}

            {/* HTML editor for Lectura type */}
            {formData.content_type === "Lectura" && (
              <div className="grid gap-2">
                <Label htmlFor="html_content">
                  <span className="flex items-center gap-1"><FileText className="h-3.5 w-3.5" /> Contenido de la lectura</span>
                </Label>
                <Suspense fallback={<div className="h-[200px] animate-pulse bg-muted rounded-md" />}>
                  <RichTextEditor
                    value={formData.html_content}
                    onChange={(html) => setFormData(prev => ({ ...prev, html_content: html }))}
                    placeholder="Escribe el contenido de la lectura..."
                  />
                </Suspense>
              </div>
            )}

            {/* Thumbnail for non-Infografía types */}
            {formData.content_type && formData.content_type !== "Infografía" && (
              <div className="grid gap-2">
                <Label htmlFor="thumbnail_url">
                  <span className="flex items-center gap-1"><Image className="h-3.5 w-3.5" /> Imagen de portada</span>
                </Label>
                {formData.content_type !== "Video" ? (
                  <FileUploadSection
                    accept="image/*"
                    label=""
                    currentUrl={formData.thumbnail_url || null}
                    onFileUploaded={(url) => setFormData(prev => ({ ...prev, thumbnail_url: url }))}
                    onClear={() => setFormData(prev => ({ ...prev, thumbnail_url: "" }))}
                  />
                ) : (
                  <Input
                    id="thumbnail_url"
                    value={formData.thumbnail_url}
                    onChange={(e) => setFormData({ ...formData, thumbnail_url: e.target.value })}
                    placeholder="https://..."
                  />
                )}
              </div>
            )}

            <div className="grid gap-2">
              <Label htmlFor="description">Descripción</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Breve descripción del contenido..."
                rows={3}
              />
            </div>

            <ContentAttachmentsEditor contentId={editingId} />

            <div className="grid gap-2">
              <Label htmlFor="tags">Etiquetas (separadas por coma)</Label>
              <Input
                id="tags"
                value={formData.tags}
                onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
                placeholder="ia, marketing, automatización"
              />
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={handleClose}>Cancelar</Button>
              <Button onClick={handleSave} disabled={isSaving}>
                {isSaving ? "Guardando..." : editingId ? "Guardar Cambios" : "Crear Contenido"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <ContentPlayerModal
        isOpen={!!previewItem}
        onClose={() => setPreviewItem(null)}
        lessonIndex={0}
        onComplete={() => {}}
        lesson={previewItem ? {
          id: previewItem.id,
          title: previewItem.name,
          duration: previewItem.duration ?? '',
          description: previewItem.description ?? '',
          type: CONTENT_TYPE_MAP[previewItem.content_type ?? ''] ?? 'reading',
          completed: false,
          thumbnail: previewItem.thumbnail_url ?? '',
          vimeo_player_url: previewItem.vimeo_player_url,
          html_content: (previewItem as any).html_content ?? null,
          url: previewItem.url,
          thumbnail_url: previewItem.thumbnail_url,
        } : null}
      />
        </TabsContent>
      </Tabs>
    </div>
  );
}

